import { DigitalWalletOwnerInfoResponse, TransactionRowResult } from "@/models/transaction";
import { Dispatch, FC, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import ArrowElement from "@/assets/images/ArrowElement.svg";
import BaseButton from "../common/baseButton";
import { formatAmountWithoutDecimals, formatDate } from "@/util";
import { useNavigate, useParams } from "react-router-dom";
import employeeBreadcrumbStore from "@/stores/employeeBreadcrumbStore";
import { ReactComponent as PhoneIcon } from "../../assets/icons/phone-small-icon.svg";

interface ITransactionsInfoTabProps {
    selectedTransaction?: TransactionRowResult | null;
    walletInfo?: DigitalWalletOwnerInfoResponse;
    setSelectedTransaction: Dispatch<SetStateAction<TransactionRowResult | null>>;
}

const TransactionsInfoTab: FC<ITransactionsInfoTabProps> = (props: ITransactionsInfoTabProps) => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();

    const openEmployeePage = (id: number | undefined, employeeName: string) => {
        if (!id) {
            return;
        }
        employeeBreadcrumbStore.update(employeeName);
        navigate({
            pathname: `/employees/${id}/info`,
            search: `?returnUrl=${window.location.pathname}`,
        });
    };

    return (
        <div className="transaction-info-tab-container">
            <div className="employee-info-tab-left">
                <div className="transaction-info-tab-header">
                    <div>{props.selectedTransaction?.employeeName}</div>
                    <div style={{ height: "25px", width: "1px", background: "#FFFFFF" }}></div>
                    <div className="transaction-info-tab-header-phone">
                        <PhoneIcon /> {props.selectedTransaction?.mobilePhone}
                    </div>
                    <div style={{ height: "25px", width: "1px", background: "#FFFFFF" }}></div>
                    <div>
                        {t("profiles.title.digitalWallet")}&nbsp;&nbsp;
                        {formatAmountWithoutDecimals(props.walletInfo?.walletAmount, props.walletInfo?.currencyId)}
                        &nbsp;&nbsp;&nbsp;
                        {formatDate(props.walletInfo?.expiredDate ?? "")}
                    </div>
                    <div>
                        {props.selectedTransaction?.employeeStatus ? (
                            <div className="status-element">
                                <div className="status-color-active"></div>
                                <div style={{ textTransform: "uppercase" }}>{t("employee.ActiveStatus")}</div>
                            </div>
                        ) : (
                            <div className="status-element">
                                <div className="status-color-inactive"></div>
                                <div style={{ textTransform: "uppercase" }}>{t("employee.InactiveStatus")}</div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="transaction-info-tab-content">
                    <div className="transaction-info-tab-content-data">
                        <div className="transaction-tab-text">
                            <div>{t("transaction.TransactionNumber") + ":"}</div>
                            <div>{t("transaction.TransactionType") + ":"}</div>
                            <div>{t("transaction.TransactionDate") + ":"}</div>
                        </div>
                        <div className="transaction-tab-data">
                            <div>{props.selectedTransaction?.id}</div>
                            <div>{t(`transaction.${props.selectedTransaction?.transactionType}`)}</div>
                            <div>{formatDate(props.selectedTransaction?.transactionDate ?? "")}</div>
                        </div>
                    </div>
                    <div className="transaction-info-tab-content-data">
                        <div className="transaction-tab-text">
                            <div>{t("transaction.TransactionAmount") + ":"}</div>
                            <div>{t("transaction.TransactionConnectedOrder") + ":"}</div>
                        </div>
                        <div className="transaction-tab-data">
                            <div className={`transaction-amount ${props.selectedTransaction?.transactionType}`}>
                                {props.selectedTransaction?.transactionAmount}
                            </div>
                            <div>
                                {props.selectedTransaction?.connectedOrderId != null ? (
                                    <div
                                        className="link-element"
                                        onClick={() =>
                                            navigate(
                                                `/digital-wallet/list/${id}/transactions/${props.selectedTransaction?.connectedOrderId}`
                                            )
                                        }
                                    >
                                        {props.selectedTransaction?.docket}
                                    </div>
                                ) : (
                                    ""
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="transaction-info-tab-content-data">
                        <div className="transaction-tab-text">
                            <div>{t("transaction.TransactionEmployeeNumber") + ":"}</div>
                        </div>
                        <div className="transaction-tab-data">
                            <div
                                className="digital-wallet-owner-info-value-link"
                                onClick={() =>
                                    openEmployeePage(props.walletInfo?.employeeId, props.walletInfo?.employeeName ?? "")
                                }
                            >
                                {props.walletInfo?.employeeNumber}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <div className="employee-info-tab-right">
                    <BaseButton
                        type="button"
                        className="basic-button"
                        onClick={() => props.setSelectedTransaction(null)}
                    >
                        <img className="back-to-list-arrow" src={ArrowElement} alt="ArrowElement" />
                        <div>{t("transaction.BackToListOfWallets")}</div>
                    </BaseButton>
                </div>
            </div>
        </div>
    );
};

export default TransactionsInfoTab;
